/**
 * Dimacon's brand (dimacon.io) for the Dimacon tenant (Miragon fork, see FORK_CHANGES.md and tenantBranding.ts):
 * the colors of the site's CSS and its logo, which has one color. Dimacon publishes no design tokens, so the three
 * shades marked as derived are computed here; Dimacon's orange reaches only 3.3:1 on white and cannot carry text.
 */
export const DIMACON_COLORS = {
  // --color-primary of dimacon.io: icons, labels and the hover of its dark buttons
  orange: "#fe5000",
  // The logo
  logo: "#e7441c",
  // --color-text-dark: text and the buttons
  dunkel: "#1d1d1d",
  // --color-text
  text: "#464646",
  // --color-light
  hell: "#fafafa",
  // --color-border
  linie: "#e0e0e0",
  // The dark sections of dimacon.io run from this navy to almost black
  navy: "#1a2342",
  navyTief: "#050912",
  // Derived: the orange darkened to 4.5:1 on Miragon's grey and on orangeFlaeche, for links and focus in light mode
  orangeText: "#c93f00",
  // Derived: the orange lightened to 4.5:1 on the darkest raised surface, for links in dark mode
  orangeHell: "#fe651f",
  // Derived: 8% orange on white, as Miragon's hover surface is 8% of its blue
  orangeFlaeche: "#fff1eb",
} as const;

export const DIMACON_WEBSITE_URL = "https://dimacon.io/";
export const DIMACON_PRIVACY_POLICY_URL = "https://dimacon.io/datenschutz/";
export const DIMACON_IMPRINT_URL = "https://dimacon.io/impressum/";

// Dimacon's chevron, the mark in front of the word and Dimacon's favicon (copies in apps/web/public/tenants)
export const DIMACON_ICON_VIEWBOX = "0 0 23.5 23.28";
export const DIMACON_ICON_SHAPES: ReadonlyArray<{ type: "path" | "polygon"; value: string }> = [
  { type: "polygon", value: "0 17.39 6.77 17.38 10.09 11.61 6.7 5.82 0 5.86 3.31 11.63 0 17.39" },
  { type: "path", value: "M16.83,0H3.16l3.44,5.64,6.77.1,3.39,5.89-3.32,5.82-6.82.12-3.45,5.71h13.62l6.71-11.65L16.83,0Z" },
];

// The whole logo from the header of dimacon.io: the chevron and DIMACON
export const DIMACON_LOGO_VIEWBOX = "0 0 154.12 23.28";
export const DIMACON_LOGO_SHAPES: ReadonlyArray<{ type: "path" | "polygon"; value: string }> = [
  { type: "polygon", value: "72.02 21.54 72 10.31 68.07 21.52 64.84 21.52 60.89 10.39 60.84 21.54 56.2 21.52 56.2 2.48 62.28 2.46 63.43 5.73 66.5 14.29 70.64 2.46 76.68 2.48 76.68 21.52 72.02 21.54" },
  { type: "path", value: "M154.09,2.46v19.06h-4.03l-7.94-11.11v11.11l-4.83-.02V2.48h4.68l7.26,10.38c.13-.4.12-.66.12-1v-1.33l-.02-6.85c0-.4.03-.81.08-1.21h4.71l-.03-.02Z" },
  { type: "path", value: "M42.91,4.35c3.6,2.57,4.18,8.29,2.55,12.06-1.11,2.55-3.32,4.26-6.02,4.81-.56.1-1.14.28-1.72.28h-8.24V2.46h8.09c1.96.2,3.8.7,5.32,1.87l.02.02ZM40.97,14.35c.5-1.38.5-2.77.2-4.1-.5-2.14-2.26-3.45-4.41-3.48l-2.36-.05v10.45c1.01,0,1.96.07,2.92-.03,1.66-.15,3.08-1.19,3.65-2.79Z" },
  { type: "path", value: "M133.17,18.38c-3.04,4.1-9.62,4.59-13.35,1.31-1.13-1-2.04-2.24-2.55-3.68-1.81-5.01-.12-11.33,5.22-13.25,2.7-.98,5.67-.75,8.16.65,1.24.7,2.19,1.63,2.95,2.84,1.31,2.02,1.76,4.41,1.54,6.83-.17,1.96-.81,3.73-1.99,5.32l.02-.02ZM126.9,17.32c1.09-.3,2.07-1.06,2.62-2.22,1.04-2.21.95-5.47-.73-7.3-.95-1.03-2.26-1.41-3.67-1.19-1.31.2-2.37,1.14-2.94,2.39-.88,1.97-.8,4.33.1,6.27.76,1.64,2.8,2.54,4.59,2.06h.02Z" },
  { type: "path", value: "M91.56,17.89h-6.92l-1.33,3.63h-4.96l4.81-12.62,2.45-6.45h5.08l4.15,10.86,3.13,8.18h-5.08l-1.34-3.62v.02ZM90.05,13.72l-1.92-5.31-1.94,5.31h3.88-.02Z" },
  { type: "path", value: "M106.12,17.37c1.74.36,3.5-.35,4.13-2.29h4.96c-.51,2.95-2.24,5.34-5.09,6.27-4.05,1.31-8.54.03-10.83-3.73-1.28-2.11-1.63-4.53-1.39-6.98.13-1.34.53-2.54,1.13-3.75,1.99-4.03,6.78-5.61,11.03-4.26,2.8.88,4.63,3.25,5.12,6.2h-4.88l-.58-.95c-.56-.93-1.51-1.29-2.55-1.34-2.59-.12-3.96,1.91-4.26,4.25-.33,2.54.35,5.95,3.18,6.57l.03.02Z" },
  { type: "polygon", value: "53.29 21.52 48.32 21.54 48.34 2.46 53.29 2.48 53.29 21.52" },
  ...DIMACON_ICON_SHAPES,
];
